import {
  type ActivityDataState,
  type ActivityMetric,
  activityDataStateLabel,
  formatActivityMetric,
} from "@dofek/format/activity-data-state";
import {
  type ActivityOverviewComparison,
  activityOverviewChangeForKey,
  createActivityOverviewComparisonFormatters,
  formatActivityOverviewChange,
} from "@dofek/format/activity-overview";
import {
  formatDateForDisplay,
  formatDateYmd,
  formatDurationMinutes,
  isToday,
  isYesterday,
  parseValidDate,
} from "@dofek/format/format";
import { formatMeasurementText } from "@dofek/format/units";
import { userFacingErrorMessage } from "@dofek/format/user-facing-error";
import { processingPollInterval } from "@dofek/providers/processing-status";
import { ACTIVITY_IMAGES_QUERY_OPTIONS } from "@dofek/training/activity-images";
import { formatActivityTypeLabel } from "@dofek/training/training";
import { Link } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ActivityCardContent, type ActivityCardData } from "../components/ActivityCardContent.tsx";
import { PageLayout } from "../components/PageLayout.tsx";
import { PaginationControls } from "../components/PaginationControls.tsx";
import { ProcessingStatusWidget } from "../components/ProcessingStatusWidget.tsx";
import { QueryStatePanel } from "../components/QueryStatePanel.tsx";
import { useProcessingStatus } from "../hooks/useProcessingStatus.ts";
import { trpc } from "../lib/trpc.ts";
import { useUnitConverter } from "../lib/unitContext.ts";

const DEFAULT_WEEKS = 4;
const ACTIVITY_PAGE_SIZE = 20;
const ALL_ACTIVITY_TYPES = "all";
const DATE_RANGE_OPTIONS = [
  { value: 4, label: "4 weeks" },
  { value: 8, label: "8 weeks" },
  { value: 12, label: "12 weeks" },
] as const;

export function ActivitiesPage() {
  const [weeks, setWeeks] = useState(DEFAULT_WEEKS);
  const [activityType, setActivityType] = useState(ALL_ACTIVITY_TYPES);
  const [showHidden, setShowHidden] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState<
    Map<
      string,
      {
        activityType: string;
        isProviderAbsent?: boolean;
      }
    >
  >(new Map());
  const selectedActivityIds = new Set(selectedActivities.keys());
  const [deletedActivityIds, setDeletedActivityIds] = useState<Set<string>>(new Set());
  const [activityPage, setActivityPage] = useState(0);
  const endDate = useMemo(() => formatDateYmd(), []);
  const units = useUnitConverter();
  const selectedActivityType = activityType === ALL_ACTIVITY_TYPES ? undefined : activityType;
  const queryInput = useMemo(
    () => ({
      weeks,
      endDate,
      ...(selectedActivityType ? { activityType: selectedActivityType } : {}),
      ...(showHidden ? { includeProviderAbsent: true } : {}),
    }),
    [weeks, endDate, selectedActivityType, showHidden],
  );
  const trpcUtils = trpc.useUtils();
  const pageInput = useMemo(
    () => ({ ...queryInput, page: activityPage, pageSize: ACTIVITY_PAGE_SIZE }),
    [queryInput, activityPage],
  );
  const rangeKey = JSON.stringify(queryInput);
  const lastSuccessfulInput = useRef<{ rangeKey: string; input: typeof pageInput } | undefined>(
    undefined,
  );
  const query = trpc.calendar.weekListV2.useQuery(pageInput, {
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) =>
      query.state.data?.days.some((day) =>
        day.activities.some((activity) => activity.location?.kind === "processing"),
      )
        ? processingPollInterval("active")
        : false,
  });
  const overviewQuery = trpc.calendar.activityOverview.useQuery(queryInput, {
    placeholderData: (previousData) => previousData,
  });
  useEffect(() => {
    if (query.data && !query.isPlaceholderData && !query.isError) {
      lastSuccessfulInput.current = { rangeKey, input: pageInput };
    }
  }, [query.data, query.isPlaceholderData, query.isError, rangeKey, pageInput]);
  const previousInput = lastSuccessfulInput.current;
  const pageData =
    query.data ??
    (previousInput?.rangeKey === rangeKey
      ? trpcUtils.calendar.weekListV2.getData(previousInput.input)
      : undefined);
  const processingStatus = useProcessingStatus({ datasets: ["activity"] });
  const bulkDelete = trpc.activity.bulkDelete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        trpcUtils.calendar.weekListV2.invalidate(),
        trpcUtils.calendar.activityOverview.invalidate(),
        trpcUtils.activity.list.invalidate(),
      ]);
      setSelectedActivities(new Map());
      setSelectMode(false);
      setConfirmDelete(false);
    },
    onError: (_error, variables) => {
      setDeletedActivityIds((current) => {
        const next = new Set(current);
        for (const activityId of variables.ids) {
          next.delete(activityId);
        }
        return next;
      });
    },
  });
  const mergeActivities = trpc.activity.merge.useMutation({
    onSuccess: async () => {
      await Promise.all([
        trpcUtils.calendar.weekListV2.invalidate(),
        trpcUtils.calendar.activityOverview.invalidate(),
        trpcUtils.calendar.calendarData.invalidate(),
        trpcUtils.activity.list.invalidate(),
      ]);
      setSelectedActivities(new Map());
      setSelectMode(false);
      setConfirmMerge(false);
    },
  });
  const restoreProviderAbsent = trpc.activity.restoreProviderAbsent.useMutation({
    onSuccess: async () => {
      await Promise.all([
        trpcUtils.calendar.weekListV2.invalidate(),
        trpcUtils.calendar.activityOverview.invalidate(),
        trpcUtils.activity.list.invalidate(),
      ]);
      setSelectedActivities(new Map());
      setSelectMode(false);
      setConfirmRestore(false);
    },
  });

  const dayGroups = useMemo(() => {
    if (!pageData) return undefined;
    return pageData.days
      .map((day) => ({
        ...day,
        activities: day.activities.filter((activity) => !deletedActivityIds.has(activity.id)),
      }))
      .filter((day) => day.activities.length > 0);
  }, [pageData, deletedActivityIds]);
  const hiddenActivityIds = new Set(
    [...selectedActivities].filter(([, activity]) => activity.isProviderAbsent).map(([id]) => id),
  );
  const hasActivities = dayGroups?.some((day) => day.activities.length > 0) ?? false;
  const activityCount = pageData?.totalCount ?? 0;
  const currentActivityPage = pageData?.page ?? activityPage;
  const visibleDayGroups = dayGroups;
  useEffect(() => {
    if (query.data && !query.isPlaceholderData) setActivityPage(query.data.page);
  }, [query.data, query.isPlaceholderData]);
  const selectedCount = selectedActivityIds.size;
  const selectedHiddenCount = [...selectedActivityIds].filter((id) =>
    hiddenActivityIds.has(id),
  ).length;
  const selectedVisibleCount = selectedCount - selectedHiddenCount;
  const selectedTypes = new Set(
    [...selectedActivities.values()]
      .filter((activity) => !activity.isProviderAbsent)
      .map((activity) => activity.activityType),
  );
  const mergeEligible =
    selectedVisibleCount >= 2 && selectedTypes.size === 1 && selectedCount === selectedVisibleCount;

  const cancelSelection = () => {
    setSelectedActivities(new Map());
    setSelectMode(false);
    setConfirmDelete(false);
    setConfirmMerge(false);
    setConfirmRestore(false);
  };

  const toggleSelectedActivity = (activity: ActivityCardData) => {
    setSelectedActivities((current) => {
      const next = new Map(current);
      if (next.has(activity.id)) {
        next.delete(activity.id);
      } else {
        next.set(activity.id, {
          activityType: activity.activityType,
          isProviderAbsent: activity.isProviderAbsent,
        });
      }
      return next;
    });
  };

  const handleConfirmDelete = () => {
    if (selectedVisibleCount === 0) return;
    const ids = [...selectedActivityIds].filter((id) => !hiddenActivityIds.has(id));
    setDeletedActivityIds((current) => new Set([...current, ...ids]));
    bulkDelete.mutate({ ids });
  };

  const handleConfirmRestore = () => {
    if (selectedHiddenCount === 0) return;
    restoreProviderAbsent.mutate({
      ids: [...selectedActivityIds].filter((id) => hiddenActivityIds.has(id)),
    });
  };

  const updateActivityType = (nextActivityType: string) => {
    setActivityType(nextActivityType);
    setActivityPage(0);
    cancelSelection();
  };

  const updateWeeks = (nextWeeks: number) => {
    setWeeks(nextWeeks);
    setActivityPage(0);
    cancelSelection();
  };

  const updateShowHidden = (nextShowHidden: boolean) => {
    setShowHidden(nextShowHidden);
    setActivityPage(0);
    cancelSelection();
  };

  return (
    <PageLayout title="Activities">
      <ProcessingStatusWidget
        data={processingStatus.data}
        error={processingStatus.error}
        loading={processingStatus.isLoading}
      />
      <div className="space-y-4">
        <ActivityControls
          activityTypes={overviewQuery.data?.activityTypes ?? []}
          activityType={activityType}
          weeks={weeks}
          showHidden={showHidden}
          canSelect={hasActivities}
          selectMode={selectMode}
          confirmDelete={confirmDelete}
          confirmMerge={confirmMerge}
          confirmRestore={confirmRestore}
          selectedCount={selectedCount}
          selectedHiddenCount={selectedHiddenCount}
          selectedVisibleCount={selectedVisibleCount}
          mergeEligible={mergeEligible}
          mergePending={mergeActivities.isPending}
          mixedTypes={selectedTypes.size > 1}
          deletePending={bulkDelete.isPending}
          restorePending={restoreProviderAbsent.isPending}
          onActivityTypeChange={updateActivityType}
          onWeeksChange={updateWeeks}
          onShowHiddenChange={updateShowHidden}
          onSelect={() => setSelectMode(true)}
          onCancelSelection={cancelSelection}
          onDeleteSelected={() => setConfirmDelete(true)}
          onMergeSelected={() => setConfirmMerge(true)}
          onRestoreSelected={() => setConfirmRestore(true)}
          onConfirmDelete={handleConfirmDelete}
          onConfirmMerge={() => mergeActivities.mutate({ ids: [...selectedActivityIds] })}
          onConfirmRestore={handleConfirmRestore}
        />
        {bulkDelete.error ? <QueryStatePanel error={bulkDelete.error} height={80} /> : null}
        {mergeActivities.error ? (
          <QueryStatePanel error={mergeActivities.error} height={80} />
        ) : null}
        {restoreProviderAbsent.error ? (
          <QueryStatePanel error={restoreProviderAbsent.error} height={80} />
        ) : null}
        {overviewQuery.isLoading && !overviewQuery.data ? (
          <QueryStatePanel variant="loading" height={120} />
        ) : overviewQuery.isError && !overviewQuery.data ? (
          <QueryStatePanel error={overviewQuery.error} height={120} />
        ) : (
          <>
            {overviewQuery.isError ? (
              <QueryStatePanel error={overviewQuery.error} height={72} />
            ) : null}
            <ActivityOverview overview={overviewQuery.data} units={units} />
          </>
        )}
      </div>
      {query.isLoading && !pageData ? (
        <QueryStatePanel variant="loading" height={400} />
      ) : query.isError && !pageData ? (
        <QueryStatePanel
          error={query.error}
          height={200}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : !dayGroups || dayGroups.length === 0 ? (
        <QueryStatePanel
          variant="empty"
          message={
            showHidden
              ? `No activities in the last ${weeks} weeks, including hidden ones.`
              : `No activities in the last ${weeks} weeks.`
          }
          height={200}
        />
      ) : (
        <div className="space-y-7">
          {query.isError ? (
            <QueryStatePanel
              error={query.error}
              height={72}
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
            />
          ) : null}
          {visibleDayGroups?.map((day) => (
            <section key={day.date}>
              <div className="mb-2 flex items-center gap-3">
                <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">
                  {formatDayHeader(day.date)}
                </h3>
                <div className="h-px flex-1 bg-border/60" />
              </div>
              <div data-testid="activity-card-grid" className="grid gap-3 lg:grid-cols-2">
                {day.activities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    units={units}
                    selectMode={selectMode}
                    selected={selectedActivityIds.has(activity.id)}
                    onToggleSelected={() => toggleSelectedActivity(activity)}
                  />
                ))}
              </div>
            </section>
          ))}
          <PaginationControls
            page={currentActivityPage}
            pageSize={ACTIVITY_PAGE_SIZE}
            totalItems={activityCount}
            itemLabel="activities"
            onPageChange={setActivityPage}
          />
        </div>
      )}
    </PageLayout>
  );
}

interface ActivityControlsProps {
  activityTypes: string[];
  activityType: string;
  weeks: number;
  showHidden: boolean;
  canSelect: boolean;
  selectMode: boolean;
  confirmDelete: boolean;
  confirmMerge: boolean;
  confirmRestore: boolean;
  selectedCount: number;
  selectedHiddenCount: number;
  selectedVisibleCount: number;
  mergeEligible: boolean;
  mergePending: boolean;
  mixedTypes: boolean;
  deletePending: boolean;
  restorePending: boolean;
  onActivityTypeChange: (activityType: string) => void;
  onWeeksChange: (weeks: number) => void;
  onShowHiddenChange: (showHidden: boolean) => void;
  onSelect: () => void;
  onCancelSelection: () => void;
  onDeleteSelected: () => void;
  onMergeSelected: () => void;
  onRestoreSelected: () => void;
  onConfirmDelete: () => void;
  onConfirmMerge: () => void;
  onConfirmRestore: () => void;
}

function ActivityControls({
  activityTypes,
  activityType,
  weeks,
  showHidden,
  canSelect,
  selectMode,
  confirmDelete,
  confirmMerge,
  confirmRestore,
  selectedCount,
  selectedHiddenCount,
  selectedVisibleCount,
  mergeEligible,
  mergePending,
  mixedTypes,
  deletePending,
  restorePending,
  onActivityTypeChange,
  onWeeksChange,
  onShowHiddenChange,
  onSelect,
  onCancelSelection,
  onDeleteSelected,
  onMergeSelected,
  onRestoreSelected,
  onConfirmDelete,
  onConfirmMerge,
  onConfirmRestore,
}: ActivityControlsProps) {
  const selectionGuidanceId = useId();
  const selectionGuidance = showHidden
    ? "Choose visible activities to merge or delete, or hidden activities to restore."
    : "Choose activities to merge or delete.";
  const selectedCountLabel = `${selectedCount} ${
    selectedCount === 1 ? "activity" : "activities"
  } selected`;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-solid p-3">
      {canSelect ? (
        <p id={selectionGuidanceId} className={selectMode ? "text-xs text-muted" : "sr-only"}>
          {selectionGuidance}
        </p>
      ) : null}
      {selectMode ? (
        <div className="flex flex-wrap items-center gap-2">
          <output
            aria-live="polite"
            aria-atomic="true"
            className="text-xs text-subtle tabular-nums"
          >
            {selectedCountLabel}
          </output>
          {confirmDelete ? (
            <>
              <span className="text-xs text-muted">
                Delete selected activities? This cannot be undone.
              </span>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={deletePending || selectedVisibleCount === 0}
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {deletePending ? "Deleting..." : "Confirm Delete"}
              </button>
            </>
          ) : confirmRestore ? (
            <>
              <span className="text-xs text-muted">
                Restore selected hidden activities? They will reappear in your activity log.
              </span>
              <button
                type="button"
                onClick={onConfirmRestore}
                disabled={restorePending || selectedHiddenCount === 0}
                className="px-3 py-1.5 text-xs rounded bg-accent text-on-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {restorePending ? "Restoring..." : "Confirm Restore"}
              </button>
            </>
          ) : confirmMerge ? (
            <>
              <span className="text-xs text-muted">
                Merge selected activities into one activity spanning their times? Original provider
                records remain.
              </span>
              <button
                type="button"
                onClick={onConfirmMerge}
                disabled={mergePending || !mergeEligible}
                className="px-3 py-1.5 text-xs rounded bg-accent text-on-accent disabled:opacity-50"
              >
                {mergePending ? "Merging..." : "Confirm Merge"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onMergeSelected}
                disabled={!mergeEligible || mergePending}
                title={mixedTypes ? "Selected activities must have the same type" : undefined}
                className="px-3 py-1.5 text-xs rounded bg-accent text-on-accent disabled:opacity-50"
              >
                Merge
              </button>
              {mixedTypes ? (
                <span className="text-xs text-muted">Select activities of one type to merge.</span>
              ) : null}
              {showHidden ? (
                <button
                  type="button"
                  onClick={onRestoreSelected}
                  disabled={restorePending || selectedHiddenCount === 0}
                  className="px-3 py-1.5 text-xs rounded bg-accent text-on-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  Restore
                </button>
              ) : null}
              <button
                type="button"
                onClick={onDeleteSelected}
                disabled={deletePending || selectedVisibleCount === 0}
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                Delete
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onCancelSelection}
            disabled={deletePending || restorePending || mergePending}
            className="px-3 py-1.5 text-xs rounded bg-accent/10 text-foreground hover:bg-surface-hover disabled:opacity-50 transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        {canSelect && !selectMode ? (
          <button
            type="button"
            onClick={onSelect}
            aria-describedby={selectionGuidanceId}
            className="sm:mr-auto px-3 py-1.5 text-xs rounded bg-accent/10 text-foreground hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Select activities
          </button>
        ) : null}
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(event) => onShowHiddenChange(event.target.checked)}
            className="h-4 w-4 accent-accent cursor-pointer"
          />
          Show hidden activities
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          Date range
          <select
            value={weeks}
            onChange={(event) => onWeeksChange(Number(event.target.value))}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-foreground"
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          Activity type
          <select
            value={activityType}
            onChange={(event) => onActivityTypeChange(event.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-foreground"
          >
            <option value={ALL_ACTIVITY_TYPES}>All activities</option>
            {activityTypes.map((type) => (
              <option key={type} value={type}>
                {formatActivityTypeLabel(type)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

interface ActivityCardProps {
  activity: ActivityCardData;
  units: ReturnType<typeof useUnitConverter>;
  selectMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}

function ActivityCard({
  activity,
  units,
  selectMode,
  selected,
  onToggleSelected,
}: ActivityCardProps) {
  const isHidden = activity.isProviderAbsent === true;
  const cardRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const photoEnabled = !activity.location && !isHidden && visible;
  const photos = trpc.activity.images.useQuery(
    { id: activity.id },
    { ...ACTIVITY_IMAGES_QUERY_OPTIONS, enabled: photoEnabled },
  );
  useEffect(() => {
    if (activity.location || isHidden || !cardRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => setVisible(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: "200px" },
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [activity.location, isHidden]);
  const cardClassName = [
    "card relative block h-full overflow-hidden transition-colors",
    selectMode ? "cursor-pointer hover:bg-surface-elevated" : "hover:bg-surface-elevated",
    selected ? "ring-2 ring-accent bg-surface-hover" : "",
    isHidden ? "border-l-4 border-l-amber-500/80 bg-amber-500/5" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const activityLabel =
    activity.name ?? formatActivityTypeLabel(activity.primaryActivityType ?? activity.activityType);
  const actionClassName =
    "text-left after:absolute after:inset-0 after:z-10 focus-visible:outline-none focus-visible:after:rounded-lg focus-visible:after:outline-2 focus-visible:after:outline-accent";
  const headingAction = selectMode ? (
    <button
      type="button"
      onClick={onToggleSelected}
      aria-pressed={selected}
      className={actionClassName}
    >
      {activityLabel}
    </button>
  ) : (
    <Link to="/activity/$id" params={{ id: activity.id }} className={actionClassName}>
      {activityLabel}
    </Link>
  );

  return (
    <article ref={cardRef} className={cardClassName}>
      <ActivityCardContent
        activity={activity}
        units={units}
        selectMode={selectMode}
        selected={selected}
        headingAction={headingAction}
        onToggleSelected={onToggleSelected}
        photo={photos.data?.images[0]}
      />
      {!activity.location && !isHidden && photos.error ? (
        <p role="alert" className="px-4 pb-3 text-xs text-red-400">
          {userFacingErrorMessage(photos.error)}
        </p>
      ) : null}
    </article>
  );
}

interface ActivityOverviewData {
  activityCount: number;
  totalMinutes: number;
  totalDistanceMeters: number | null;
  totalDistanceState: ActivityDataState;
  totalElevationGainM: number | null;
  totalElevationState: ActivityDataState;
  comparison?: ActivityOverviewComparison;
}

type ActivityOverviewItem =
  | { key: "activityCount"; label: string; value: string }
  | { key: "totalMinutes"; label: string; value: string }
  | (ActivityMetric & { key: "totalDistanceMeters" })
  | (ActivityMetric & { key: "totalElevationGainM" });

function ActivityOverview({
  overview,
  units,
}: {
  overview: ActivityOverviewData | undefined;
  units: ReturnType<typeof useUnitConverter>;
}) {
  const items: ActivityOverviewItem[] = overview
    ? [
        { key: "activityCount", label: "Activities", value: String(overview.activityCount) },
        { key: "totalMinutes", label: "Time", value: formatDurationMinutes(overview.totalMinutes) },
        {
          key: "totalDistanceMeters",
          ...formatActivityMetric(
            "Distance",
            overview.totalDistanceMeters,
            overview.totalDistanceState,
            (distanceMeters) => formatMeasurementText(units.formatDistance(distanceMeters / 1000)),
          ),
        },
        {
          key: "totalElevationGainM",
          ...formatActivityMetric(
            "Elevation",
            overview.totalElevationGainM,
            overview.totalElevationState,
            (elevationMeters) => formatMeasurementText(units.formatElevation(elevationMeters)),
          ),
        },
      ]
    : [
        { key: "activityCount", label: "Activities", value: "Loading…" },
        { key: "totalMinutes", label: "Time", value: "Loading…" },
        { key: "totalDistanceMeters", status: "missing", label: "Distance", reason: "Loading…" },
        {
          key: "totalElevationGainM",
          status: "missing",
          label: "Elevation",
          reason: "Loading…",
        },
      ];
  const formatComparisonMagnitude = createActivityOverviewComparisonFormatters(units);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map((item) => {
        const isMetric = "status" in item;
        const comparison = overview?.comparison
          ? activityOverviewChangeForKey(overview.comparison, item.key)
          : undefined;
        return (
          <div
            key={item.key}
            className="rounded-lg border border-border bg-surface-solid p-3"
            data-state={isMetric ? item.status : undefined}
          >
            <div className="text-lg font-semibold tabular-nums">
              {isMetric && item.status !== "available"
                ? `${item.label} ${activityDataStateLabel(item.status)}`
                : item.value}
            </div>
            <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-muted">
              {isMetric && item.status !== "available" ? item.reason : item.label}
            </div>
            {comparison ? (
              <div className="mt-1 text-xs text-subtle">
                {formatActivityOverviewChange(
                  comparison,
                  overview?.comparison?.periodLabel ?? "previous period",
                  formatComparisonMagnitude[item.key],
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function formatDayHeader(dateStr: string): string {
  const date = parseValidDate(`${dateStr}T00:00:00`);
  if (!date) return dateStr;
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return formatDateForDisplay(date);
}
